import { Segmented, Typography, Row, Button, Input, Upload, message, DatePicker, DatePickerProps, Switch } from "antd"
import { trainingAPI } from "@api/TrainingAPI.js";
import { useState, useEffect } from "react";
import { CloudUploadOutlined } from "@ant-design/icons";
import { UploadRequestOption } from "rc-upload/lib/interface";
import { DataSourceType } from "./consts";
import { useSelector } from "react-redux";
import Papa from 'papaparse';
import dayjs, { Dayjs } from 'dayjs';
import axios from 'axios';

const { Text } = Typography
const { TextArea } = Input;
const { Dragger } = Upload;

export const DataSource = ({
    unitKey,
    integrationName,
    dataSource,
    setDataSource,
    dataSourceSubmit
}: IDataSource) => {

    let {
        info: {
            integrations
        },
    } = useSelector(({ team }) => team);

    const [apiKey, setApiKey] = useState<string | undefined>(unitKey === 'firstbeat' ? 'Using existing API key' : undefined);
    const [csvData, setCsvData] = useState<string[][] | undefined>(undefined);
    const [disableApiTextbox, setDisableApiTextbox] = useState<boolean>(unitKey === 'firstbeat')
    const [fileList, setFileList] = useState<any[]>([]);
    const [date, setDate] = useState<string | undefined>();
    const [datePickerValue, setDatePickerValue] = useState<Dayjs | null>(null);
    const requiresConfiguredApiKey = unitKey === 'firstbeat'
    const [configuredApiKey, setConfiguredApiKey] = useState<boolean>(unitKey === 'firstbeat')
    const [loading, setLoading] = useState<boolean>(false)
    const [findDatesLoading, setFindDatesLoading] = useState<boolean>(false)
    const [trainingDates, setTrainingDates] = useState<string[]>([])
    const [showTrainingDates, setShowTrainingDates] = useState<boolean>(false)

    useEffect(() => {
        if (integrations?.gps && unitKey) {
            const defaultInteg: IIntegrations = integrations.gps.find((integ: IIntegrations) => integ.key === unitKey);
            if (defaultInteg) setDataSource(defaultInteg.type === "csv" ? "csv" : "api");
        }
    }, [unitKey, integrations])

    const csvRequest = (options: UploadRequestOption) => {
        const file = options.file as File
        setFileList([file])

        const reader = new FileReader();
        reader.onload = () => {
            const content = reader.result as string;
            const result = Papa.parse<string[]>(content, {
                skipEmptyLines: true,
            });
            setCsvData(result.data);

            if (options.onSuccess) {
                options.onSuccess('ok');
            }
        };

        reader.onerror = () => {
            console.error('File reading error:', reader.error);
            if (options.onError) {
                options.onError(reader.error as Error);
            }
        };

        reader.readAsText(file);
    };

    const onChangeDatePicker: DatePickerProps['onChange'] = (date, dateString) => {
        setDate(dateString)
        setDatePickerValue(date)
    };

    const handleGetStarted = async (targetDate?: string) => {
        setLoading(true)
        if (dataSource === 'api') {
            const dateToUse = targetDate || date;
            if (!dateToUse) {
                message.error({content: "Please select valid date to fetch sample data.", duration: 5})
                setLoading(false);
                return 
            }

            if (configuredApiKey) {
                await dataSourceSubmit({
                    use_configured_api_key: true,
                    target_date: dateToUse
                })
            } else {
                if (!apiKey) {
                    message.error({content: "Please enter valid API credentials to fetch sample data.", duration: 5})
                    setLoading(false);
                    return 
                }
                await dataSourceSubmit({
                    api_key: apiKey,
                    use_configured_api_key: false,
                    target_date: dateToUse
                })
            }
        } else if (dataSource === 'csv') {
            await dataSourceSubmit({
                csv: csvData
            })
        }
        setLoading(false)
    };

    return (
        <>
            <Segmented
                block
                options={[
                    { label: 'API Key Input', value: 'api' },
                    { label: 'CSV File Upload', value: 'csv' }
                ]}
                value={dataSource}
                onChange={(value: string | number) => { setDataSource(value as any) }}
                style={{ marginBottom: '10px' }}
            />

            {dataSource === 'api' &&
                <>
                    <Row style={{ justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                        <Text strong>API Key</Text>

                        <div>
                            <Text style={{ marginRight: '4px' }}>Use my existing API key</Text>
                            <Switch
                                checked={configuredApiKey}
                                disabled={requiresConfiguredApiKey}
                                onChange={async (checked: boolean) => {
                                    if (checked) {
                                        setApiKey('Using existing API key')
                                    } else {
                                        setApiKey('')
                                    }

                                    setConfiguredApiKey(checked)
                                    setDisableApiTextbox(checked)
                                }}
                            />
                        </div>
                    </Row>

                    <TextArea
                        rows={10}
                        maxLength={3000}
                        disabled={disableApiTextbox}
                        value={apiKey}
                        onChange={(e) => {
                            setApiKey((e as any).target.value)
                        }}
                        style={{ marginBottom: '10px' }}
                    />

                    <Text strong style={{ marginRight: '10px' }}>Please select a date with trainings to get a sample data point:</Text>

                    <Row style={{ marginTop: '10px', alignItems: 'center', flexWrap: 'wrap', gap: '10px' }}>
                        <DatePicker 
                            onChange={onChangeDatePicker} 
                            placeholder="Target Date" 
                            style={{ borderRadius: '8px' }}
                            value={datePickerValue}
                        />
                        
                        <Button
                            onClick={async () => {
                                setFindDatesLoading(true)
                                setShowTrainingDates(false)
                                try {
                                    const dates = await trainingAPI.getTrainingDates(integrationName, 3)
                                    setTrainingDates(dates)
                                    setShowTrainingDates(true)
                                } catch (error) {
                                    if (axios.isAxiosError(error)) {
                                        const statusCode = error.response?.status;
                                        const responseData = error.response?.data;

                                        if (statusCode === 428) {
                                            message.error(responseData || `Can't connect to ${integrationName}`)
                                        } else {
                                            console.error('Error fetching training dates:', error)
                                            message.error('Failed to fetch training dates')
                                        }

                                    } else {
                                        console.error('Error fetching training dates:', error)
                                        message.error('Failed to fetch training dates')
                                    }

                                    setTrainingDates([])
                                }
                                setFindDatesLoading(false)
                            }}
                            loading={findDatesLoading}
                        >
                            Find dates
                        </Button>

                        {showTrainingDates && trainingDates.length > 0 && (
                            <div style={{ padding: '8px 12px', backgroundColor: '#f5f5f5', borderRadius: '8px', display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '8px' }}>
                                <Text strong>Recent Training Dates:</Text>
                                {trainingDates.map((trainingDate, index) => (
                                    <Button
                                        key={index}
                                        size="small"
                                        onClick={async () => {
                                            setDate(trainingDate)
                                            const dateValue = dayjs(trainingDate)
                                            setDatePickerValue(dateValue)
                                            message.success(`Date ${trainingDate} selected`)
                                            // Trigger Get Started button with the selected date
                                            await handleGetStarted(trainingDate)
                                        }}
                                    >
                                        {trainingDate}
                                    </Button>
                                ))}
                            </div>
                        )}

                        {showTrainingDates && trainingDates.length === 0 && (
                            <div style={{ padding: '8px 12px', backgroundColor: '#fff7e6', borderRadius: '8px' }}>
                                <Text type="warning">No training dates found</Text>
                            </div>
                        )}
                    </Row>
                </>
            }

            {dataSource === 'csv' &&
                <Dragger
                    className="csv-upload"
                    name='file'
                    multiple={false}
                    customRequest={csvRequest}
                    beforeUpload={(file) => {
                        const isCsv = file.type === 'text/csv'

                        if (!isCsv) {
                            message.error(`${file.name} is not a csv file`)
                        }

                        return isCsv || Upload.LIST_IGNORE
                    }}
                    accept=".csv"
                    maxCount={1}
                    itemRender={() => {
                        return <div />
                    }}
                    fileList={fileList}
                >
                    <p className="ant-upload-drag-icon">
                        <CloudUploadOutlined />
                    </p>
                    <Row style={{ 'justifyContent': 'center' }}>
                        <Text>Drop file here</Text>
                    </Row>
                    <Row style={{ 'justifyContent': 'center' }}>
                        <Text type="secondary">Supported format: .csv</Text>
                    </Row>
                    <div style={{ height: '20px' }}>
                        {fileList && fileList.length > 0 && (
                            <div>
                                {fileList.map((file) => (
                                    <div key={(file as any).uid}>
                                        {(file as any).name}
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </Dragger>
            }

            <Row style={{ marginTop: '20px' }}>
                <Button
                    type="primary"
                    onClick={() => handleGetStarted()}
                    loading={loading}
                >
                    Get Started
                </Button>
            </Row>

        </>
    )
}

interface IDataSource {
    unitKey: string,
    integrationName: string,
    dataSource: DataSourceType,
    setDataSource: (ds: DataSourceType) => void,
    dataSourceSubmit: (data: object) => Promise<void>
}

interface IIntegrations {
    key: string,
    url: string,
    type: string,
    name: string
}

export default DataSource